import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, providersAPI } from '../services/api';
import { Provider, Booking } from '../types';
import { AlertCircle, BarChart3, CheckCircle, Banknote, TrendingUp, Filter, Calendar, Clock, User, Mail, Phone, MapPin, Navigation, XCircle, Shield, FileCheck, Megaphone, AlertTriangle, Wallet } from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Booking state
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bookingFilters, setBookingFilters] = useState({
    status: '',
    providerId: '',
    customerId: ''
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingFilters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bookingsResponse, analyticsResponse, providersResponse] = await Promise.all([
        adminAPI.getAllBookings(),
        adminAPI.getBookingAnalytics(),
        providersAPI.getProviders()
      ]);
      setBookings(bookingsResponse.data?.bookings || []);
      setAnalytics(analyticsResponse.data || null);
      setProviders(providersResponse.data?.providers || []);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await adminAPI.getAllBookings(bookingFilters);
      setBookings(response.data.bookings);
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      setError(error.response?.data?.message || 'Failed to load bookings');
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Booking management handlers
  const handleAcceptBooking = async (bookingId: string) => {
    try {
      setError('');
      setSuccess('');
      
      await adminAPI.acceptBooking(bookingId);
      setSuccess('Booking accepted successfully!');
      fetchBookings();
    } catch (error: any) {
      console.error('Error accepting booking:', error);
      setError(error.response?.data?.message || 'Failed to accept booking');
    }
  };

  const handleCancelBooking = async (bookingId: string) => {
    const reason = prompt('Please provide a reason for cancellation (optional):');
    
    try {
      setError('');
      setSuccess('');
      
      await adminAPI.cancelBooking(bookingId, reason || undefined);
      setSuccess('Booking cancelled successfully!');
      fetchBookings();
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      setError(error.response?.data?.message || 'Failed to cancel booking');
    }
  };

  const handleCompleteBooking = async (bookingId: string) => {
    try {
      setError('');
      setSuccess('');
      
      await adminAPI.completeBooking(bookingId);
      setSuccess('Booking marked as completed successfully!');
      fetchBookings();
    } catch (error: any) {
      console.error('Error completing booking:', error);
      setError(error.response?.data?.message || 'Failed to complete booking');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-300">This page is only accessible to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-gray-700 mt-2">Booking Analytics & Management</p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <button
            onClick={() => navigate('/admin/kyc-approval')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left border-2 border-transparent hover:border-black"
          >
            <div className="flex items-center">
              <div className="bg-gray-100 rounded-lg p-3">
                <Shield className="h-6 w-6 text-black" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-black">KYC Approvals</h3>
                <p className="text-sm text-gray-600">Review and approve KYC documents</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/police-verification')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left border-2 border-transparent hover:border-black"
          >
            <div className="flex items-center">
              <div className="bg-gray-100 rounded-lg p-3">
                <FileCheck className="h-6 w-6 text-black" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Police Verification</h3>
                <p className="text-sm text-gray-600">Review police verification documents</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/users')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left border-2 border-transparent hover:border-black"
          >
            <div className="flex items-center">
              <div className="bg-gray-100 rounded-lg p-3">
                <User className="h-6 w-6 text-black" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
                <p className="text-sm text-gray-600">Manage users and roles</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/marketing-notification')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left border-2 border-transparent hover:border-black"
          >
            <div className="flex items-center">
              <div className="bg-pink-100 rounded-lg p-3">
                <Megaphone className="h-6 w-6 text-pink-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Marketing</h3>
                <p className="text-sm text-gray-600">Send push notifications</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/complaints')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left border-2 border-transparent hover:border-red-500"
          >
            <div className="flex items-center">
              <div className="bg-red-100 rounded-lg p-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Issues</h3>
                <p className="text-sm text-gray-600">View and manage complaints</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/analytics')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left border-2 border-transparent hover:border-green-500"
          >
            <div className="flex items-center">
              <div className="bg-green-100 rounded-lg p-3">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Revenue</h3>
                <p className="text-sm text-gray-600">Track revenue and analytics</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/pricing')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left border-2 border-transparent hover:border-yellow-500"
          >
            <div className="flex items-center">
              <div className="bg-yellow-100 rounded-lg p-3">
                <Banknote className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Pricing</h3>
                <p className="text-sm text-gray-600">Manage driver pricing slabs</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/distance-ranges')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left border-2 border-transparent hover:border-purple-500"
          >
            <div className="flex items-center">
              <div className="bg-purple-100 rounded-lg p-3">
                <MapPin className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Distance</h3>
                <p className="text-sm text-gray-600">Configure search ranges</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/feature-flags')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left border-2 border-transparent hover:border-indigo-500"
          >
            <div className="flex items-center">
              <div className="bg-indigo-100 rounded-lg p-3">
                <Wallet className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Feature Flags & Costs</h3>
                <p className="text-sm text-gray-600">Manage feature flags & fixed costs</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/city-coverage')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left border-2 border-transparent hover:border-green-500"
          >
            <div className="flex items-center">
              <div className="bg-green-100 rounded-lg p-3">
                <MapPin className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">City & Coverage</h3>
                <p className="text-sm text-gray-600">Manage cities and service coverage areas</p>
              </div>
            </div>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Total Bookings</h3>
                <p className="text-2xl font-bold text-blue-600">{analytics?.overview?.totalBookings || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Completed</h3>
                <p className="text-2xl font-bold text-green-600">{analytics?.overview?.completedBookings || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <Banknote className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Total Revenue</h3>
                <p className="text-2xl font-bold text-purple-600">₹{typeof analytics?.overview?.totalRevenue === 'number' ? analytics.overview.totalRevenue.toFixed(2) : '0.00'}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-orange-500" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Pending Revenue</h3>
                <p className="text-2xl font-bold text-orange-600">₹{typeof analytics?.overview?.pendingRevenue === 'number' ? analytics.overview.pendingRevenue.toFixed(2) : '0.00'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-600">{success}</p>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center space-x-4">
            <Filter className="h-5 w-5 text-gray-500" />
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              value={bookingFilters.status}
              onChange={(e) => setBookingFilters({ ...bookingFilters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <label className="text-sm font-medium text-gray-700">Provider:</label>
            <select
              value={bookingFilters.providerId}
              onChange={(e) => setBookingFilters({ ...bookingFilters, providerId: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              <option value="">All Providers</option>
              {providers?.map((provider) => (
                <option key={provider._id} value={provider._id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading bookings...</p>
          </div>
        ) : bookings?.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
            <p className="text-gray-600">No bookings match the selected filters.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {bookings?.map((booking) => {
              const isDriver = booking.serviceType === 'driver' || booking.pickupLocation;
              
              const getStatusIcon = (status: string) => {
                switch (status) {
                  case 'confirmed':
                    return <CheckCircle className="h-5 w-5 text-green-500" />;
                  case 'pending':
                    return <AlertCircle className="h-5 w-5 text-yellow-500" />;
                  case 'completed':
                    return <CheckCircle className="h-5 w-5 text-blue-500" />;
                  case 'cancelled':
                    return <XCircle className="h-5 w-5 text-red-500" />;
                  default:
                    return <AlertCircle className="h-5 w-5 text-gray-500" />;
                }
              };

              const getStatusColor = (status: string) => {
                switch (status) {
                  case 'confirmed':
                    return 'bg-green-100 text-green-800 border-green-200';
                  case 'pending':
                    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
                  case 'completed':
                    return 'bg-blue-100 text-blue-800 border-blue-200';
                  case 'cancelled':
                    return 'bg-red-100 text-red-800 border-red-200';
                  default:
                    return 'bg-gray-100 text-gray-800 border-gray-200';
                }
              };

              return (
                <div key={booking._id || booking.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                  {/* Header with Gradient */}
                  <div className="bg-black px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                          {isDriver ? <Navigation className="h-6 w-6 text-white" /> : <Calendar className="h-6 w-6 text-white" />}
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">
                            {booking.serviceType || 'Service'} Booking
                          </h3>
                          <p className="text-purple-100 text-sm">
                            ID: {(booking.bookingId || booking._id || booking.id)?.toString().slice(-8).toUpperCase()}
                          </p>
                        </div>
                      </div>
                      <div className={`px-4 py-2 rounded-lg border-2 flex items-center space-x-2 ${getStatusColor(booking.status)}`}>
                        {getStatusIcon(booking.status)}
                        <span className="font-semibold text-sm">
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-6">
                    <div className="grid md:grid-cols-3 gap-6 mb-6">
                      {/* Customer Info */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <User className="h-4 w-4 mr-2 text-blue-600" />
                          Customer
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p className="font-medium text-gray-900">
                            {booking.customer?.name || (typeof booking.customerId === 'object' ? booking.customerId?.name : null) || 'N/A'}
                          </p>
                          {booking.customer?.phone && (
                            <a href={`tel:${booking.customer.phone}`} className="flex items-center text-blue-600 hover:text-blue-700">
                              <Phone className="h-3 w-3 mr-2" />
                              {booking.customer.phone}
                            </a>
                          )}
                          {booking.customer?.email && (
                            <a href={`mailto:${booking.customer.email}`} className="flex items-center text-blue-600 hover:text-blue-700 text-xs">
                              <Mail className="h-3 w-3 mr-2" />
                              {booking.customer.email}
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Provider Info */}
                      <div className="bg-purple-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <User className="h-4 w-4 mr-2 text-purple-600" />
                          Provider
                        </h4>
                        <div className="flex items-center space-x-3">
                          {booking.provider?.avatar ? (
                            <img
                              src={booking.provider.avatar}
                              alt={booking.provider.name}
                              className="h-10 w-10 rounded-full object-cover border-2 border-purple-200 flex-shrink-0"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-purple-200 flex items-center justify-center border-2 border-purple-300 flex-shrink-0">
                              <span className="text-purple-700 font-semibold text-sm">
                                {(booking.provider?.name || (typeof booking.providerId === 'object' ? booking.providerId?.name : null) || 'N')?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="space-y-2 text-sm flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {booking.provider?.name || (typeof booking.providerId === 'object' ? booking.providerId?.name : null) || 'N/A'}
                            </p>
                            {booking.provider?.phone && (
                              <a href={`tel:${booking.provider.phone}`} className="flex items-center text-purple-600 hover:text-purple-700">
                                <Phone className="h-3 w-3 mr-2 flex-shrink-0" />
                                <span className="truncate">{booking.provider.phone}</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Booking Info */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <Clock className="h-4 w-4 mr-2 text-black" />
                          Schedule & Payment
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Start DateTime:</span>
                            <span className="font-medium text-gray-900 text-xs">
                              {booking.tripStartDateTime
                                ? new Date(booking.tripStartDateTime).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  })
                                : 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">End DateTime:</span>
                            <span className="font-medium text-gray-900 text-xs">
                              {booking.tripEndDateTime
                                ? new Date(booking.tripEndDateTime).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  })
                                : 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Booked On:</span>
                            <span className="font-medium text-gray-900 text-xs">
                              {booking.createdAt
                                ? new Date(booking.createdAt).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  })
                                : new Date(booking.bookingDate).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                            </span>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                            <span className="text-gray-600">Amount:</span>
                            <span className="font-bold text-black flex items-center">
                              <Banknote className="h-4 w-4 mr-1" />
                              {(() => {
                                // Always convert to string first, remove any currency symbols, then parse
                                const amountStr = String(booking.totalAmount || '0');
                                // Remove all currency symbols ($, ₹, commas, and any whitespace)
                                const cleaned = amountStr.replace(/[$₹,\s]/g, '').trim();
                                const numValue = parseFloat(cleaned);
                                return isNaN(numValue) ? '₹0.00' : `₹${numValue.toFixed(2)}`;
                              })()}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Payment:</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium text-black ${
                              booking.paymentStatus === 'completed' ? 'bg-green-100' :
                              booking.paymentStatus === 'failed' ? 'bg-red-100' :
                              'bg-yellow-100'
                            }`}>
                              {booking.paymentStatus}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Driver Trip Route - if driver booking */}
                    {isDriver && booking.pickupLocation && booking.dropoffLocation && (
                      <div className="bg-blue-50 rounded-lg p-4 mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-blue-600" />
                          Trip Route
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                              <MapPin className="h-4 w-4 text-white" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-green-700 mb-1">PICKUP</p>
                              <p className="text-sm text-gray-900">{booking.pickupLocation.address}</p>
                            </div>
                          </div>
                          <div className="ml-4 border-l-2 border-gray-300 h-4"></div>
                          <div className="flex items-start">
                            <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
                              <MapPin className="h-4 w-4 text-white" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-red-700 mb-1">DROP-OFF</p>
                              <p className="text-sm text-gray-900">{booking.dropoffLocation.address}</p>
                            </div>
                          </div>
                          {booking.totalDistance && (
                            <div className="bg-white rounded-lg p-3 flex items-center justify-between mt-2">
                              <span className="text-sm text-gray-600">Distance</span>
                              <span className="text-sm font-bold text-gray-900">{typeof booking.totalDistance === 'number' ? booking.totalDistance.toFixed(2) : booking.totalDistance || '0.00'} km</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {booking.notes && (
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Notes:</span> {booking.notes}
                        </p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {booking.status !== 'completed' && booking.status !== 'cancelled' && (
                      <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                        {booking.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleAcceptBooking(booking._id || booking.id || '')}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center shadow-sm"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Accept
                            </button>
                            <button
                              onClick={() => handleCancelBooking(booking._id || booking.id || '')}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium flex items-center shadow-sm"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Cancel
                            </button>
                          </>
                        )}
                        {booking.status === 'confirmed' && (
                          <button
                            onClick={() => handleCompleteBooking(booking._id || booking.id || '')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center shadow-sm"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark Complete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
